import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import type { VoteReason } from "../../services/generated/client.js";
import { rateCard, type GameLink } from "../../services/gameLink.js";

/**
 * "How was that card?" — the signal half of phase 13.
 *
 * # It is a different question from the flag, and it says so
 *
 * `FlagPanel` asks *is this year wrong?* and can void the round. This asks
 * whether the **card** was any good — the audio is not the song (phase 1g stored
 * a dubstep remix as Lady Gaga and only a listener would ever notice), the clip
 * is broken, nobody in the room could have known it. It changes nothing about
 * the game: no coins, no phase, no round.
 *
 * The two live side by side at `Reveal`, and the copy has to keep them apart —
 * somebody who presses 👎 because they lost the round is noise.
 *
 * # 👍 / 👎 / **why** — phase 18, finding #7
 *
 * The room asked for it in these words: *"the guys agree we should change the
 * way we vote a good song — like, dislike and why, easy to understand."* The old
 * copy was abstract — *"how was that card?"*, *"good card"*, *"something's
 * off"* — with a lead that had to explain the question. **If the copy needs a
 * disclaimer to be understood, the copy is the problem.**
 *
 * ⚠️ **But like/dislike changes what the vote means, and this vote is
 * destructive.** Two distinct parties voting off suspend a track from the deal
 * for the entire installation. A room that dislikes a perfectly good song is not
 * evidence of anything wrong with it — so renaming the buttons without
 * separating the meanings would convert a catalog-quality signal into a taste
 * signal *and leave the destructive trigger wired to it*.
 *
 * The **why does the sorting**, because a reason is something a player can
 * answer honestly and a category is something the server can act on. Only
 * `wrong_year` and `bad_clip` count toward a suspension; *nobody knew it* is a
 * familiarity signal and *didn't like it* is taste. ⚠️ A 👎 with **no** reason
 * counts as neither — decided, because an unexplained dislike is the taste case
 * by default.
 *
 * So the room gets its two buttons, curators get a reason instead of a bare
 * boolean, and `PARTIES_TO_SUSPEND` keeps meaning what it means.
 *
 * # What pressing it actually does, stated
 *
 * Nothing immediate, and saying so is the honest version: one room's opinion is
 * one opinion, and it takes **two independent parties** before a card stops
 * being dealt. A button implying it removed the song would be a lie the first
 * time somebody played the same card an hour later.
 *
 * # No confirmation step and no undo
 *
 * It is one tap, it is free, and pressing the other one replaces it — the server
 * keeps one vote per person per track. A dialog on top of a reveal is how a
 * feature nobody needs becomes a feature nobody uses.
 */
export function RateCard({ link }: { link: GameLink }) {
  const { t } = useTranslation();
  // The acknowledgement is worded **here**, not by the server. `RateAck` does
  // carry a sentence, but it is English whatever the phone is set to, and this
  // screen already knows everything it says.
  const [answer, setAnswer] = useState<string | null>(null);
  // 👎 is one tap and the reason is a second, skippable one — which is how the
  // room gets a two-button screen and the catalog still gets a category.
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = (keep: boolean, reasonCode: VoteReason | null) => {
    setBusy(true);
    void rateCard(link, keep, null, reasonCode)
      .then((ack) => {
        setAnswer(
          !ack.recorded
            ? t("rate.ack.noCard")
            : keep
              ? t("rate.ack.keep")
              : t("rate.ack.off"),
        );
      })
      .catch(() => {
        // A rating that did not save is worth nothing to a player mid-reveal,
        // and a red box during the best moment of the round is worse than a
        // lost vote.
        setAnswer(null);
        setAsking(false);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (answer) {
    return (
      <section data-testid="rate-card" className="rounded-lg border border-white/10 p-3">
        <p className="text-xs text-white/50">{answer}</p>
      </section>
    );
  }

  if (asking) {
    return (
      <section data-testid="rate-card" className="rounded-lg border border-white/10 p-3">
        {/* Both ids on one prompt: `rate-card` is *"the rating is on screen"*,
            which the stage asserts; `rate-why` is *"it is asking the follow-up
            question"*, which this component's own tests assert. */}
        <h2 data-testid="rate-why" className="text-sm font-semibold">
          {t("rate.why")}
        </h2>
        {/* Said plainly, because it is the difference between a vote that
            removes a song and one that does not — and a player who does not know
            that cannot answer honestly. */}
        <p className="mt-1 text-xs text-white/50">{t("rate.whyLead")}</p>
        <div className="mt-2 flex flex-col gap-2">
          {REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              disabled={busy}
              data-testid="rate-reason"
              data-reason={reason}
              onClick={() => {
                send(false, reason);
              }}
              className="min-h-12 rounded-lg border border-white/20 px-3 text-left text-sm font-medium disabled:opacity-40"
            >
              {reasonLabel(reason, t)}
            </button>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              // No reason given, and that is a real answer: it records the 👎
              // and counts toward nothing.
              send(false, null);
            }}
            className="min-h-12 rounded-lg text-sm text-white/50 disabled:opacity-40"
          >
            {t("rate.skip")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="rate-card" className="rounded-lg border border-white/10 p-3">
      <h2 className="text-sm font-semibold">{t("rate.title")}</h2>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            send(true, null);
          }}
          className="min-h-12 flex-1 rounded-lg border border-white/20 text-sm font-medium disabled:opacity-40"
        >
          {t("rate.good")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAsking(true);
          }}
          className="min-h-12 flex-1 rounded-lg border border-white/20 text-sm font-medium disabled:opacity-40"
        >
          {t("rate.off")}
        </button>
      </div>
    </section>
  );
}

/** Every reason, in the order they are offered. Matches `VoteReason::ALL`. */
const REASONS: readonly VoteReason[] = [
  "wrong_year",
  "bad_clip",
  "unknown_song",
  "disliked",
];

/**
 * One reason's sentence.
 *
 * Spelled out rather than templated, so a reason added to the Rust enum is a
 * `tsc` error here until it has copy — the property the typed `t` exists for.
 */
function reasonLabel(
  reason: VoteReason,
  // ⚠️ `TFunction`, not `ReturnType<typeof useTranslation>["t"]`. The latter
  // carries the whole generated key union through every call, and the tree is
  // large enough now that TypeScript gives up with "type instantiation is
  // excessively deep" — in *this* file, while the key that tipped it was added
  // somewhere else entirely. Keys stay statically checked either way.
  t: TFunction,
): string {
  switch (reason) {
    case "wrong_year":
      return t("rate.reason.wrong_year");
    case "bad_clip":
      return t("rate.reason.bad_clip");
    case "unknown_song":
      return t("rate.reason.unknown_song");
    case "disliked":
      return t("rate.reason.disliked");
  }
}
